<template>
    <div class="company-information-form-locked">
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.country_label')"
            orientation="horizontal"
        >
            <CompassTooltip
                :content="tooltipText"
                :arrow="false"
                :placement="CompassEnums.TooltipPosition.BOTTOM_START"
            >
                <span class="typographies-paragraph-medium-regular">
                    {{ countryName }}
                </span>
            </CompassTooltip>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.siren_label')"
            orientation="horizontal"
        >
            <CompassTooltip
                :content="tooltipText"
                :arrow="false"
                :placement="CompassEnums.TooltipPosition.BOTTOM_START"
            >
                <span class="typographies-paragraph-medium-regular">
                    {{ data.legal_france.siren }}
                </span>
            </CompassTooltip>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.siret_label')"
            orientation="horizontal"
        >
            <CompassTooltip
                :content="tooltipText"
                :arrow="false"
                :placement="CompassEnums.TooltipPosition.BOTTOM_START"
            >
                <span class="typographies-paragraph-medium-regular">
                    {{ data.legal_france.siret }}
                </span>
            </CompassTooltip>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.company_name_label')"
            orientation="horizontal"
        >
            <CompassTooltip
                :content="tooltipText"
                :arrow="false"
                :placement="CompassEnums.TooltipPosition.BOTTOM_START"
            >
                <span class="typographies-paragraph-medium-regular">
                    {{ data.legal_name }}
                </span>
            </CompassTooltip>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.vat_number_label')"
            orientation="horizontal"
        >
            <CompassTooltip
                :content="tooltipText"
                :arrow="false"
                :placement="CompassEnums.TooltipPosition.BOTTOM_START"
            >
                <span class="typographies-paragraph-medium-regular">
                    {{ data.legal_france.vat }}
                </span>
            </CompassTooltip>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.legal_status_label')"
            :error="errors.legalStatus"
            orientation="horizontal"
        >
            <CompassInput
                v-model="legalStatus"
                :error="!!errors.legalStatus"
                @input="errors.legalStatus = ''"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.share_capital_label')"
            :error="errors.shareCapital"
            orientation="horizontal"
        >
            <CompassInput
                v-model="shareCapital"
                :error="!!errors.shareCapital"
                @input="errors.shareCapital = ''"
            >
                <template v-if="showCurrencyLeft" #prefix>
                    {{ numformat.currencysymbol }}
                </template>
                <template v-if="showCurrencyRight" #suffix>
                    {{ numformat.currencysymbol }}
                </template>
            </CompassInput>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.naf_code_label')"
            :error="errors.naf"
            orientation="horizontal"
        >
            <CompassInput v-model="naf" :error="!!errors.naf" @input="errors.naf = ''" />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.rcs_label')"
            :error="errors.rcs"
            orientation="horizontal"
        >
            <CompassInput v-model="rcs" :error="!!errors.rcs" @input="errors.rcs = ''" />
        </CompassFormItem>
        <CompanyInformationFormAdvice />
        <CompassButton
            class="company-information-form-locked__update-button"
            :label="$t.__('common.actions.update')"
            :loading="isLoading"
            :type="CompassEnums.ButtonType.TERTIARY"
            @click="handleSubmit"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue';
import { Translator, PushToast } from 'js/shims-vue';
import { updateAccountInformation } from 'api/account/account';
import CompanyInformationFormAdvice from 'vcomponents/preferences/CompanyInformation/CompanyInformationFormAdvice.vue';
import type {
    CompanyData,
    Country,
} from 'vcomponents/preferences/CompanyInformation/CompanyInformation.vue';
import { useStore } from 'vuex';

interface Props {
    data: CompanyData;
    countries: Country[];
}

const props = defineProps<Props>();

const emit = defineEmits(['update']);

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

// Store
const store = useStore();

const legalStatus = ref<string>(props.data.legal_france.company_type || '');
const shareCapital = ref<string>(props.data.capital || '');
const naf = ref<string>(props.data.legal_france.ape_naf_code || '');
const rcs = ref<string>(props.data.legal_france.rcs_immatriculation || '');
const isLoading = ref<boolean>(false);

const tooltipText = computed(() => $t.__('CompanyInformation.lock.tooltip'));
const numformat = computed(() => store.state.corp.numformat);
const showCurrencyLeft = computed((): boolean => {
    return numformat.value.currencypos === 'left' || numformat.value.currencypos === 'both';
});
const showCurrencyRight = computed((): boolean => {
    return numformat.value.currencypos === 'right' || numformat.value.currencypos === 'both';
});

watch(
    () => props.data,
    (newData): void => {
        legalStatus.value = newData.legal_france.company_type || '';
        shareCapital.value = newData.capital || '';
        naf.value = newData.legal_france.ape_naf_code || '';
        rcs.value = newData.legal_france.rcs_immatriculation || '';
    },
    { deep: true },
);
const errors = ref({
    legalStatus: '',
    shareCapital: '',
    naf: '',
    rcs: '',
});

const countryName = computed((): string => {
    if (!props.data.country_code || props.countries.length === 0) {
        return '';
    }
    const country = props.countries.find(
        (country) => country.code.toLowerCase() === props.data.country_code.toLowerCase(),
    );
    return country?.name || '';
});

const handleSubmit = async (): Promise<void> => {
    isLoading.value = true;
    try {
        const payload: Record<string, unknown> = {
            legal_france: {
                company_type: legalStatus.value || null,
                ape_naf_code: naf.value || null,
                rcs_immatriculation: rcs.value || null,
            },
            capital: shareCapital.value || null,
        };

        const fieldsToValidate = [
            'legal_france.company_type',
            'legal_france.ape_naf_code',
            'legal_france.rcs_immatriculation',
            'capital',
        ];

        const response = await updateAccountInformation(payload, fieldsToValidate);
        emit('update', response.data);
        $pushToast({
            type: 'success',
            text: $t.__('CompanyInformation.legal_information.save.success'),
        });
    } catch (error: unknown) {
        const apiError = (
            error as { response?: { data?: { error?: { details?: Record<string, string> } } } }
        )?.response?.data?.error?.details;
        const fieldMap: Record<string, keyof typeof errors.value> = {
            company_type: 'legalStatus',
            capital: 'shareCapital',
            ape_naf_code: 'naf',
            rcs_immatriculation: 'rcs',
        };
        if (apiError) {
            for (const key of Object.keys(apiError)) {
                const shortenedKey = key.split('.').pop() as string;
                if (fieldMap[shortenedKey]) {
                    errors.value[fieldMap[shortenedKey]] = apiError[key];
                }
            }
        }
        $pushToast({
            type: 'error',
            text: $t.__('CompanyInformation.legal_information.save.error'),
        });
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
.company-information-form-locked {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);

    &__update-button {
        display: block;
        margin-left: auto;
    }

    :deep() {
        .compass-form-item--horizontal .compass-form-item__label--medium {
            min-height: auto;
        }
    }
}
</style>
